import React, { useEffect, useMemo, useRef, useState } from "react";
import { ResponsiveStream } from "@nivo/stream";
import PropTypes from "prop-types";
import { Empty } from "antd";
import { useLocation, useSearchParams } from "react-router-dom";
import { fetchClaraResponseCategorizationByDateForIframe } from "../services/api";
import {
  ENV_TYPE_PROD,
  CATEGORY_TYPE_CLARA_RESPONSE,
  CATEGORY_TYPE_USER_QUERY,
} from "../constants";

const EMPTY_CHART_DATA = {
  streamData: [],
  keys: [],
  dates: [],
};

/**
 * Observe the component container rather than the browser viewport.
 * This keeps the chart responsive inside narrow iframes and dashboard panels.
 */
const useElementSize = () => {
  const elementRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const element = elementRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;

      const width = Math.round(entry.contentRect.width);
      const height = Math.round(entry.contentRect.height);

      setSize((current) =>
        current.width === width && current.height === height
          ? current
          : { width, height },
      );
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [elementRef, size];
};

const ClaraFeedbackTrends = ({
  id: propId,
  source: propSource,
  envType: propEnvType,
}) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const isIframeRoute = location.pathname.includes("/iframe/");
  const id = searchParams.get("id") || propId || null;
  const source = searchParams.get("source") || propSource || "invite-clara";
  const darkMode = searchParams.get("darkMode") === "true";
  const envType = propEnvType ?? ENV_TYPE_PROD;

  const [data, setData] = useState(EMPTY_CHART_DATA);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryType, setCategoryType] = useState(CATEGORY_TYPE_USER_QUERY);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [containerRef, containerSize] = useElementSize();
  const containerWidth = containerSize.width;
  
  // Fallback to aspect ratio for ultra-wide detection (original behavior)
  const isUltraWideAspect = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth / window.innerHeight > 2.2;
  }, []);
  
  const isNarrow = containerWidth > 0 && containerWidth < 640;
  const isCompact = containerWidth > 0 && containerWidth < 900;
  const isWide = (containerWidth > 0 && containerWidth >= 1200) || isUltraWideAspect;

  const colorByKey = useMemo(
    () => {
      if (data.keys.length === 0) return {};
      
      const goldenRatioConjugate = 0.618033988749895;
      
      // Create deterministic starting hue from hash of all data keys
      let seedHash = 0;
      const seedStr = data.keys.join('|');
      for (let i = 0; i < seedStr.length; i++) {
        seedHash = seedStr.charCodeAt(i) + ((seedHash << 5) - seedHash);
      }
      
      let huePosition = (Math.abs(seedHash) % 1000000) / 1000000;
      
      return Object.fromEntries(
        data.keys.map((key, index) => {
          // Iterate hue using golden ratio for optimal spacing
          huePosition += goldenRatioConjugate;
          huePosition %= 1;
          
          const hue = Math.floor(huePosition * 360);
          const saturation = 65 + (index % 3) * 10; // 65%, 75%, 85%
          const lightness = 50 + (Math.floor(index / 3) % 4) * 5; // 50%, 55%, 60%, 65%
          
          return [String(key), `hsl(${hue}, ${saturation}%, ${lightness}%)`];
        })
      );
    },
    [data.keys],
  );

  // ResponsiveStream assigns array colors in the same order as `keys`.
  const streamColors = useMemo(
    () => data.keys.map((key) => colorByKey[String(key)]),
    [data.keys, colorByKey],
  );

  const chartLayout = useMemo(() => {
    if (containerWidth > 0 && containerWidth < 640) {
      return {
        margin: { top: 24, right: 12, bottom: 84, left: 46 },
        maxTicks: 5,
        tickRotation: -45,
        fontSize: 10,
        showLegend: false,
        legendOffset: 64,
      };
    }

    if (containerWidth > 0 && containerWidth < 900) {
      return {
        margin: { top: 30, right: 24, bottom: 82, left: 54 },
        maxTicks: 7,
        tickRotation: -40,
        fontSize: 11,
        showLegend: false,
        legendOffset: 62,
      };
    }

    if (containerWidth > 0 && containerWidth < 1200) {
      return {
        margin: { top: 40, right: 150, bottom: 72, left: 62 },
        maxTicks: 10,
        tickRotation: -30,
        fontSize: 12,
        showLegend: true,
        legendOffset: 54,
      };
    }

    return {
      margin: { top: 44, right: 190, bottom: 68, left: 66 },
      maxTicks: 12,
      tickRotation: -25,
      fontSize: 12,
      showLegend: true,
      legendOffset: 52,
    };
  }, [containerWidth]);

  const tickValues = useMemo(() => {
    const totalDates = data.dates.length;

    if (totalDates === 0) return [];
    if (totalDates <= chartLayout.maxTicks) {
      return data.dates.map((_, index) => index);
    }

    const interval = Math.ceil(totalDates / chartLayout.maxTicks);
    const values = [];

    for (let index = 0; index < totalDates; index += interval) {
      values.push(index);
    }

    const lastIndex = totalDates - 1;
    if (values[values.length - 1] !== lastIndex) {
      values.push(lastIndex);
    }

    return values;
  }, [data.dates, chartLayout.maxTicks]);

  const legends = useMemo(() => {
    if (!chartLayout.showLegend) return [];

    return [
      {
        anchor: "bottom-right",
        direction: "column",
        translateX: isWide ? 170 : 132,
        itemWidth: isWide ? 160 : 118,
        itemHeight: 20,
        itemsSpacing: 2,
        itemTextColor: darkMode ? "#e5e7eb" : "#374151",
        symbolSize: 11,
        symbolShape: "circle",
        effects: [
          {
            on: "hover",
            style: {
              itemTextColor: darkMode ? "#ffffff" : "#111827",
            },
          },
        ],
      },
    ];
  }, [chartLayout.showLegend, darkMode, isWide]);

  useEffect(() => {
    let isCurrent = true;

    const fetchData = async () => {
      if (!source) {
        if (isCurrent) {
          setError("Missing required parameter: source is required");
          setIsLoading(false);
          setIsInitialLoad(false);
        }
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await fetchClaraResponseCategorizationByDateForIframe(
          id,
          source,
          envType,
          categoryType,
        );
        if (!isCurrent) return;
        if (!response.success) {
          throw new Error(response.error || "Failed to fetch data");
        }
        const categoryField =
          categoryType === CATEGORY_TYPE_USER_QUERY
            ? "user_query_category"
            : "clara_response_category";
        const categoryMap = new Map();
        const dateSet = new Set();
        (response.data || []).forEach((item) => {
          const category = item[categoryField];
          const date = item.creation_date;
          if (!category || !date) return;

          dateSet.add(date);

          if (!categoryMap.has(category)) {
            categoryMap.set(category, new Map());
          }

          // Sum duplicates instead of allowing the last record to overwrite them.
          const dateMap = categoryMap.get(category);
          const currentCount = dateMap.get(date) || 0;
          dateMap.set(date, currentCount + (Number(item.count) || 0));
        });

        const dates = Array.from(dateSet).sort((first, second) =>
          String(first).localeCompare(String(second)),
        );
        const keys = Array.from(categoryMap.keys()).sort((first, second) =>
          String(first).localeCompare(String(second)),
        );

        const streamData = dates.map((date) =>
          Object.fromEntries(
            keys.map((key) => [key, categoryMap.get(key)?.get(date) ?? 0]),
          ),
        );

        setData({ streamData, keys, dates });
      } catch (requestError) {
        if (!isCurrent) return;

        console.error("Error fetching Clara data:", requestError);
        setError(
          requestError.response?.status === 403
            ? "Access forbidden. This iframe can only be embedded on authorized domains."
            : requestError.message || "Failed to fetch data",
        );
      } finally {
        if (isCurrent) {
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      }
    };

    fetchData();

    return () => {
      isCurrent = false;
    };
  }, [id, source, envType, categoryType]);

  if (isInitialLoad && isLoading) {
    return (
      <div
        className={`fixed inset-0 z-50 flex min-h-screen w-full items-center justify-center ${
          darkMode ? "bg-gray-900" : "bg-gray-50"
        }`}
      >
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-300 border-t-blue-500" />
          <span
            className={`text-xl font-medium ${
              darkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Checking Authentication...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`fixed inset-0 z-50 flex min-h-screen w-full items-center justify-center p-4 ${
          darkMode ? "bg-gray-900" : "bg-gray-50"
        }`}
      >
        <div
          className={`w-full max-w-lg rounded-lg p-6 text-center shadow-lg sm:p-8 ${
            darkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          <h1
            className={`mb-4 text-3xl font-bold ${
              darkMode ? "text-red-400" : "text-red-600"
            }`}
          >
            Error
          </h1>
          <p
            className={`text-sm ${
              darkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            {error}
          </p>
        </div>
      </div>
    );
  }

  if (data.streamData.length === 0) {
    return (
      <div className={`flex min-h-screen w-full items-center justify-center p-2 sm:p-4 lg:p-6 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
        <div
          className={`flex min-h-[420px] w-full max-w-screen-2xl flex-col items-center justify-center rounded-lg p-3 sm:p-5 lg:p-6 text-center ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span className={darkMode ? 'text-gray-300' : 'text-gray-500'}>No data</span>
            }
          />
        </div>
      </div>
    );
  }

  const getButtonClassName = (type) =>
    `min-w-0 rounded-md px-3 py-2 text-sm font-medium transition-all sm:px-4 ${
      categoryType === type
        ? "text-white shadow-sm"
        : darkMode
          ? "text-gray-300 hover:bg-gray-600"
          : "text-gray-700 hover:bg-gray-100"
    } ${isLoading ? "cursor-not-allowed opacity-50" : ""}`;

  return (
    <div
      className={`min-h-screen overflow-auto p-2 sm:p-4 lg:p-6 ${
        darkMode ? "bg-gray-900" : "bg-white"
      }`}
      style={{ scrollbarGutter: "stable" }}
    >
      <div className="mx-auto w-full max-w-screen-2xl">
        <div
          ref={containerRef}
          className={`w-full rounded-lg p-3 sm:p-5 lg:p-6 ${
            darkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          <div className={isWide ? "flex items-center gap-6" : ""}>
            {/* Title and Toggle Section */}
            {isWide ? (
              <div
                className="flex-shrink-0 flex items-center justify-center"
                style={{ width: "120px", height: "400px" }}
              >
                <div
                  style={{
                    transform: "rotate(-90deg)",
                    transformOrigin: "center center",
                    width: "600px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <h1
                    className={`text-2xl font-bold sm:text-3xl whitespace-nowrap text-center ${
                      darkMode ? "text-gray-100" : "text-gray-800"
                    }`}
                  >
                    Clara Trends Over Time
                  </h1>

                  {isIframeRoute && (
                    <div className="mt-4 flex justify-center">
                      <div
                        className={`inline-flex gap-1 rounded-lg border p-1 ${
                          darkMode
                            ? "border-gray-600 bg-gray-700"
                            : "border-gray-300 bg-white"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => setCategoryType(CATEGORY_TYPE_USER_QUERY)}
                          disabled={isLoading}
                          className={`rounded-md px-4 py-2 text-sm font-medium transition-all whitespace-nowrap ${
                            categoryType === CATEGORY_TYPE_USER_QUERY
                              ? "text-white shadow-sm"
                              : darkMode
                                ? "text-gray-300 hover:bg-gray-600"
                                : "text-gray-700 hover:bg-gray-100"
                          } ${isLoading ? "cursor-not-allowed opacity-50" : ""}`}
                          style={categoryType === CATEGORY_TYPE_USER_QUERY ? { backgroundColor: "#2f6f57" } : {}}
                        >
                          User Query Category
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setCategoryType(CATEGORY_TYPE_CLARA_RESPONSE)
                          }
                          disabled={isLoading}
                          className={`rounded-md px-4 py-2 text-sm font-medium transition-all whitespace-nowrap ${
                            categoryType === CATEGORY_TYPE_CLARA_RESPONSE
                              ? "text-white shadow-sm"
                              : darkMode
                                ? "text-gray-300 hover:bg-gray-600"
                                : "text-gray-700 hover:bg-gray-100"
                          } ${isLoading ? "cursor-not-allowed opacity-50" : ""}`}
                          style={categoryType === CATEGORY_TYPE_CLARA_RESPONSE ? { backgroundColor: "#2f6f57" } : {}}
                        >
                          Clara Response Category
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <header
                className={`flex min-w-0 flex-col items-center text-center`}
              >
                <h1
                  className={`text-2xl font-bold sm:text-3xl ${
                    darkMode ? "text-gray-100" : "text-gray-800"
                  }`}
                >
                  Clara Trends Over Time
                </h1>

                {isIframeRoute && (
                  <div className={`mt-4 w-full max-w-xl`}>
                    <div
                      className={`grid w-full grid-cols-2 gap-1 rounded-lg border p-1 ${
                        darkMode
                          ? "border-gray-600 bg-gray-700"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setCategoryType(CATEGORY_TYPE_USER_QUERY)}
                        disabled={isLoading}
                        className={getButtonClassName(
                          CATEGORY_TYPE_USER_QUERY,
                        )}
                        style={categoryType === CATEGORY_TYPE_USER_QUERY ? { backgroundColor: "#2f6f57" } : {}}
                      >
                        User Query Category
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setCategoryType(CATEGORY_TYPE_CLARA_RESPONSE)
                        }
                        disabled={isLoading}
                        className={getButtonClassName(
                          CATEGORY_TYPE_CLARA_RESPONSE,
                        )}
                        style={categoryType === CATEGORY_TYPE_CLARA_RESPONSE ? { backgroundColor: "#2f6f57" } : {}}
                      >
                        Clara Response Category
                      </button>
                    </div>
                  </div>
                )}
              </header>
            )}

            <section className={`relative min-w-0 ${isWide ? "flex-1" : ""}`}>
              {!isInitialLoad && isLoading && (
                <div
                  className={`absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-opacity-75 ${
                    darkMode ? "bg-gray-800" : "bg-white"
                  }`}
                  aria-live="polite"
                >
                  <span
                    className={`text-lg font-medium ${
                      darkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Updating chart...
                  </span>
                </div>
              )}

              <div
                className="w-full min-w-0"
                style={{ height: "clamp(420px, 65vh, 680px)" }}
              >
                <ResponsiveStream
                  data={data.streamData}
                  keys={data.keys}
                  margin={chartLayout.margin}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    orient: "bottom",
                    tickSize: 5,
                    tickPadding: 6,
                    tickRotation: chartLayout.tickRotation,
                    legend: "Date",
                    legendOffset: chartLayout.legendOffset,
                    tickValues,
                    format: (value) => {
                      const dateIndex = Number.parseInt(value, 10);
                      return data.dates[dateIndex] || value;
                    },
                  }}
                  axisLeft={{
                    orient: "left",
                    tickSize: 5,
                    tickPadding: 6,
                    tickRotation: 0,
                    legend: "Count",
                    legendOffset: isCompact ? -40 : -50,
                  }}
                  offsetType="none"
                  colors={streamColors}
                  fillOpacity={0.82}
                  borderWidth={1}
                  borderColor={{ from: "color", modifiers: [["darker", 0.7]] }}
                  enableGridX={!isNarrow}
                  enableGridY
                  curve="catmullRom"
                  theme={{
                    axis: {
                      ticks: {
                        text: {
                          fontSize: chartLayout.fontSize,
                          fill: darkMode ? "#e5e7eb" : "#374151",
                        },
                      },
                      legend: {
                        text: {
                          fontSize: chartLayout.fontSize,
                          fontWeight: 600,
                          fill: darkMode ? "#e5e7eb" : "#374151",
                        },
                      },
                    },
                    grid: {
                      line: {
                        stroke: darkMode ? "#374151" : "#e5e7eb",
                        strokeWidth: 1,
                      },
                    },
                    tooltip: {
                      container: {
                        background: darkMode ? "#1f2937" : "#ffffff",
                        color: darkMode ? "#e5e7eb" : "#374151",
                      },
                    },
                  }}
                  legends={legends}
                  tooltip={({ layer }) => (
                    <div
                      className={`rounded-xl border p-3 shadow-xl ${
                        darkMode
                          ? "border-gray-600 bg-gray-800"
                          : "border-gray-200 bg-white"
                      }`}
                    >
                      <div
                        className={`mb-1 font-semibold ${
                          darkMode ? "text-gray-100" : "text-gray-800"
                        }`}
                      >
                        {layer.id}
                      </div>
                      <div
                        className={`text-sm ${
                          darkMode ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Count: {layer.value}
                      </div>
                    </div>
                  )}
                  animate={data.keys.length <= 30}
                  motionConfig="gentle"
                />
              </div>

              {!chartLayout.showLegend && (
                <div className="mt-3 flex flex-wrap justify-center gap-x-4 gap-y-2 px-2">
                  {data.keys.map((key) => (
                    <div
                      key={key}
                      className={`flex items-center gap-2 text-xs ${
                        darkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: colorByKey[String(key)] }}
                      />
                      <span>{key}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

ClaraFeedbackTrends.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  source: PropTypes.oneOf(["invite-clara", "wtw-clara"]),
  envType: PropTypes.oneOf([0, 1, 2]),
};

export default ClaraFeedbackTrends;
